/** Display form for a per-church ticket number: 1 → 0001. */
export const formatTicketNumber = (number: number): string => String(number).padStart(4, '0');

export const SUPPORT_TICKET_CATEGORIES = [
  'billing',
  'giving',
  'access',
  'bug',
  'how_to',
  'other',
] as const;

export const SUPPORT_TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

export const SUPPORT_TICKET_STATUSES = [
  'open',
  'awaiting_church',
  'awaiting_platform',
  'escalated',
  'resolved',
  'closed',
] as const;

export const OPEN_SUPPORT_TICKET_STATUSES = [
  'open',
  'awaiting_church',
  'awaiting_platform',
  'escalated',
] as const;

export type SupportTicketCategory = (typeof SUPPORT_TICKET_CATEGORIES)[number];
export type SupportTicketPriority = (typeof SUPPORT_TICKET_PRIORITIES)[number];
export type SupportTicketStatus = (typeof SUPPORT_TICKET_STATUSES)[number];

export interface SupportTicketSummary {
  id: string;
  churchId: string;
  churchName?: string;
  number: number;
  subject: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;
  createdByUserId: string;
  createdByName: string | null;
  assignedPlatformUserId: string | null;
  assignedPlatformName: string | null;
  linkedImpersonationId: string | null;
  escalationLevel: number;
  slaFirstResponseDueAt: string;
  slaResolveDueAt: string;
  firstPlatformReplyAt: string | null;
  escalatedAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  lastMessageAt: string;
  lastMessageSide: 'church' | 'platform';
  createdAt: string;
  updatedAt: string;
}

export interface SupportTicketAttachment {
  id: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  kind: 'image' | 'document';
  downloadUrl: string | null;
  messageId: string | null;
  createdAt: string;
}

export interface SupportTicketMessage {
  id: string;
  authorUserId: string;
  authorName: string | null;
  authorSide: 'church' | 'platform';
  body: string;
  isInternal: boolean;
  attachments: SupportTicketAttachment[];
  createdAt: string;
}

export interface SupportTicketEvent {
  id: string;
  actorUserId: string | null;
  actorName: string | null;
  actorSide: 'church' | 'platform' | 'system';
  kind:
    | 'created'
    | 'message'
    | 'assigned'
    | 'unassigned'
    | 'priority_changed'
    | 'status_changed'
    | 'escalated'
    | 'visit_linked'
    | 'visit_started'
    | 'resolved'
    | 'reopened'
    | 'closed';
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown> | null;
  messageId: string | null;
  createdAt: string;
}

export interface SupportTicketTimeline {
  ticket: SupportTicketSummary;
  messages: SupportTicketMessage[];
  events: SupportTicketEvent[];
}

const isOpenForSla = (status: SupportTicketStatus): boolean =>
  status !== 'resolved' && status !== 'closed';

export const isFirstReplyOverdue = (
  ticket: Pick<
    SupportTicketSummary,
    'status' | 'firstPlatformReplyAt' | 'slaFirstResponseDueAt'
  >,
  now = Date.now(),
): boolean =>
  isOpenForSla(ticket.status) &&
  ticket.firstPlatformReplyAt === null &&
  now > Date.parse(ticket.slaFirstResponseDueAt);

export const isResolutionOverdue = (
  ticket: Pick<SupportTicketSummary, 'status' | 'escalationLevel' | 'slaResolveDueAt'>,
  now = Date.now(),
): boolean =>
  isOpenForSla(ticket.status) && ticket.escalationLevel >= 1 && now > Date.parse(ticket.slaResolveDueAt);

export const isSlaBreached = (ticket: SupportTicketSummary, now = Date.now()): boolean =>
  isFirstReplyOverdue(ticket, now) || isResolutionOverdue(ticket, now);
